import React, { useEffect } from "react";

import { Action, Category } from "@/constants/events";
import { INITIAL_RESULT_LENGTH, SEARCH_INITIAL_TAKE } from "@/constants/search";
import { Dispensary } from "@/custom-types/Dispensary";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import useSearch from "@/hooks/useSearch";
import { useSelector } from "@/redux/hooks";
import { getUserId } from "@/redux/selectors/user";
import { getRoundedDistance } from "@/utils/distance";

import Button from "@/components/botanic/Button";
import DispensaryCard from "@/components/botanic/DispensaryCard";
import DispensaryCardPlaceholder from "@/components/botanic/DispensaryCard/DispensaryCardPlaceholder";
import Image from "@/components/Image";
import SearchViewMoreButton from "@/components/search/SearchViewMoreButton";

const DispensaryResults: React.FC = () => {
  const domainCountryCode = useDomainCountryCode();
  const { publishEvent } = useEventTracker();

  const userId = useSelector(getUserId);

  const {
    dispensary,
    loading,
    searchCategory,
    searchTerm,
    loadMoreSearchResults,
  } = useSearch();

  const isCategoryTab = searchCategory === "dispensary";

  useEffect(() => {
    if (isCategoryTab && dispensary.length === SEARCH_INITIAL_TAKE) {
      loadMoreSearchResults("dispensary", 7);
    }
  }, [isCategoryTab]);

  const dispensaries = isCategoryTab
    ? dispensary
    : dispensary.slice(0, INITIAL_RESULT_LENGTH);

  return (
    <>
      <h2>Locations</h2>
      <p className="mb-lg">matching "{searchTerm}"</p>
      <div className="grid md:grid-cols-4 gap-lg">
        {loading
          ? new Array(isCategoryTab ? 8 : INITIAL_RESULT_LENGTH)
              .fill(null)
              .map((_, i) => (
                <DispensaryCardPlaceholder
                  id={`dispensary-card-placeholder-${i}`}
                  key={`dispensary-card-placeholder-${i}`}
                  variant="platinum"
                  hideCTA
                />
              ))
          : dispensaries.map((dispensary: Dispensary) => {
              const {
                currentStatuses,
                deliveryEnabled,
                distanceMi,
                id,
                inStoreCartEnabled,
                logoUrl,
                name,
                pickupEnabled,
                preorderConfigured,
                rating,
                reviewCount,
                slug,
                tags,
                timeZone,
              } = dispensary;

              const sharedEventParams = {
                category: Category.retailerCard,
                dispensaryDistance: getRoundedDistance(
                  distanceMi,
                  domainCountryCode,
                ),
                dispensaryId: id,
                userId,
              };

              return (
                <DispensaryCard
                  currentStatus={currentStatuses?.store}
                  deliveryEnabled={deliveryEnabled}
                  dispensaryId={id}
                  distanceMi={distanceMi}
                  hideCTA
                  inStoreCartEnabled={inStoreCartEnabled}
                  key={id}
                  logoUrl={logoUrl}
                  name={name}
                  onClick={() =>
                    publishEvent({
                      action: Action.click,
                      ...sharedEventParams,
                    })
                  }
                  pickupEnabled={pickupEnabled}
                  preorderConfigured={preorderConfigured}
                  preserveHeight
                  rating={rating}
                  reviewCount={reviewCount}
                  scheduleType="store"
                  slug={slug}
                  tags={tags}
                  timeZone={timeZone}
                  trackImpression={() =>
                    publishEvent({
                      action: Action.impression,
                      ...sharedEventParams,
                    })
                  }
                  variant="platinum"
                  leaflyListBadge={dispensary?.flags?.includes(
                    "leaflyListBadge",
                  )}
                />
              );
            })}
      </div>

      <SearchViewMoreButton category="dispensary" />

      <div className="relative flex flex-col justify-center items-center mt-section border border-light-grey py-24 px-lg text-center">
        <Image
          className="opacity-50"
          sizes={[510, 734, null, 990, null, 510]}
          src="https://leafly-public.imgix.net/global-search/seattle-map-lg.png"
          alt=""
          background
        />

        <h3 className="text-md font-bold relative mb-lg">
          Looking to find a nearby dispensary?
        </h3>

        <Button
          data-testid="view-map-button"
          href="/dispensaries"
          variant="secondary"
          className="relative bg-white max-w-[300px]"
          onClick={() =>
            publishEvent({
              action: Action.click,
              category: Category.viewDispensaryMap,
            })
          }
        >
          view dispensary map
        </Button>
      </div>
    </>
  );
};

export default DispensaryResults;
